// ═══════════════════════════════════════════════════════════════════════════
// RÉGIMES SPÉCIAUX — SOURCE UNIQUE DE LA DÉTECTION (14/08/2026)
//
// POURQUOI CE FICHIER EXISTE — la question du fondateur : « pourquoi tu ne vois
// pas toutes ces failles, pourquoi l'app ne tourne pas à 100 % ». Réponse
// MESURÉE le 14/08/2026 : l'app sait encoder DIX régimes spéciaux, et 46
// fichiers re-devinaient le régime chacun dans son coin, avec sa propre liste
// de champs. Deux d'entre eux ont explosé le même jour :
//
//   • la route DmfA ne reconnaissait pas une étudiante → ONSS PLEINE au lieu de
//     la solidarité 8,13 %, et ses heures hors du contingent de 650 h ;
//   • à la ligne suivante, la porte FLEXI lisait exactement les mêmes champs
//     fantômes — même trou, jamais remarqué.
//
// La cause n'est pas l'inattention : c'est qu'il n'y avait AUCUN endroit unique
// où la question « quel est le régime de ce travailleur ? » se pose. Deux vagues
// de rustines avaient déjà allongé les listes de champs ; le trou est revenu.
// Ce fichier est l'endroit unique.
//
// ⚠️ PÉRIMÈTRE — CE FICHIER NE PORTE AUCUNE VALEUR LÉGALE (R1). Il répond à UNE
// question : « quel régime ? ». Les taux, plafonds, quotas et codes DmfA vivent
// dans `lois-belges.js`, avec leurs sources datées. Ne jamais écrire un chiffre
// ici — le jour où on le fera, il divergera de la source.
//
// ⚠️ OÙ VIT LE RÉGIME, en vrai (mesuré sur des fiches réelles) : il y a DEUX
// encodages, et il faut les deux.
//   • L'écran fiche employé range le régime dans `statut` (« flexijob »,
//     « apprenti »…) — via `statutPourBase()`.
//   • L'import et le générateur de contrat le rangent dans le TYPE DE CONTRAT
//     (colonne `contract_type`, ou `data.contract`).
//   Le dossier qui a révélé le trou portait « etudiant » dans `contract_type` ET
//   `data.contract`, avec `data.statut = 'employe'`.
// ═══════════════════════════════════════════════════════════════════════════

/** JSONB `data`, tolérant à la chaîne. */
function _data(emp) {
  let d = emp?.data ?? emp?._dataJson ?? {};
  if (typeof d === 'string') { try { d = JSON.parse(d); } catch (_) { d = {}; } }
  return d && typeof d === 'object' ? d : {};
}

/**
 * Minuscule, sans accents — « Étudiant » et « etudiant » sont le même mot.
 *
 * ⚠️ `\p{Diacritic}` et NON la plage [U+0300-U+036F] écrite en clair : cette
 * plage ne s'écrit qu'avec des accents combinants NUS, invisibles dans un
 * éditeur, qu'un copier-coller peut avaler sans bruit — le filtre redeviendrait
 * alors silencieusement faux. Règle posée le 01/08/2026 et verrouillée par
 * `__tests__/etudiant-accent-detection.test.js`, qui a attrapé cette ligne
 * le 14/08 : j'avais écrit la plage nue en créant ce fichier.
 */
function _norm(v) {
  return String(v ?? '').normalize('NFD').replace(/\p{Diacritic}/gu, '').trim().toLowerCase();
}

// ── Les dix régimes ────────────────────────────────────────────────────────
//
// `valeurs`  : valeurs EXACTES du champ de statut (ce que l'écran écrit).
// `motifTexte` : motif cherché DANS UN TEXTE LIBRE de type de contrat. Il n'est
//                donné qu'aux régimes qui s'écrivent réellement comme un type de
//                contrat (« Contrat étudiant », « contrat d'apprentissage »).
//
// ⚠️ Trois régimes n'ont VOLONTAIREMENT pas de motif texte libre, et c'est un
// choix de sûreté, pas un oubli :
//   • volontaire → « heures supplémentaires VOLONTAIRES » est une notion
//     courante et sans rapport ; un motif large ferait basculer des salariés
//     ordinaires en régime volontariat ;
//   • artiste / sportif → ces mots apparaissent dans des intitulés de fonction
//     (« directeur artistique », « coach sportif ») ; la fonction n'est pas le
//     régime.
// Un faux positif ici ne se voit pas à l'œil et se paie en cotisations.
export const REGIMES_SPECIAUX = [
  { code: 'etudiant',       valeurs: ['etudiant', 'student', 'jobiste'],                      motifTexte: /etud|student|jobist/ },
  { code: 'flexijob',       valeurs: ['flexijob', 'flexi', 'flexi_job', 'flexi-job'],         motifTexte: /flexi/ },
  { code: 'apprenti',       valeurs: ['apprenti', 'apprentissage', 'alternance'],             motifTexte: /apprenti|alternance/ },
  { code: 'interimaire',    valeurs: ['interimaire', 'interim'],                              motifTexte: /interim/ },
  { code: 'extras_horeca',  valeurs: ['extras_horeca', 'extra_horeca', 'extras', 'extra'],    motifTexte: /extra.?horeca/ },
  { code: 'article17',      valeurs: ['article17', 'article_17', 'art17', 'art_17'],          motifTexte: /article\s*17/ },
  { code: 'pensionneActif', valeurs: ['pensionneactif', 'pensionne_actif', 'pensionne'],      motifTexte: null },
  { code: 'artiste',        valeurs: ['artiste'],                                             motifTexte: null },
  { code: 'sportif',        valeurs: ['sportif'],                                             motifTexte: null },
  { code: 'volontaire',     valeurs: ['volontaire', 'benevole'],                              motifTexte: null },
];

export const CODES_REGIMES_SPECIAUX = REGIMES_SPECIAUX.map((r) => r.code);

/** Champs qui portent le STATUT choisi à l'écran (valeur exacte attendue). */
function _champsStatut(emp) {
  const d = _data(emp);
  return [emp?.statut, emp?.sousStatut, emp?.status_type, emp?.statut_type, d.statut, d.sousStatut, d.regimeSpecial];
}

/**
 * Champs qui portent le TYPE DE CONTRAT (texte libre possible).
 *
 * ⚠️ La liste est longue parce que la base l'est : `contract_type` est la
 * colonne, mais l'import, le générateur de contrat et de vieilles fiches
 * écrivent ailleurs. `type_contrat` (avec l'underscore à l'envers) est un alias
 * RÉEL — il a été rajouté ici le 14/08 parce que le test des accents l'a vu
 * manquer. Retirer une entrée de cette liste, c'est rendre des travailleurs
 * invisibles à leur propre régime : ne jamais l'élaguer « pour faire propre ».
 */
function _champsContrat(emp) {
  const d = _data(emp);
  return [emp?.contract_type, emp?.contractType, emp?.contract, emp?.type, emp?.type_contrat,
    d.contract, d.contractType, d.contract_type, d.type, d.type_contrat, d.contractLabel];
}

/**
 * Quel régime spécial ? — `null` si le travailleur est un salarié ordinaire.
 *
 * L'ordre de `REGIMES_SPECIAUX` fait la priorité et il est DÉLIBÉRÉ : étudiant,
 * flexi puis apprenti d'abord, parce que ce sont ceux qui changent le calcul des
 * cotisations et l'ouverture des droits. Une fiche ne devrait porter qu'un seul
 * régime ; si elle en porte deux, on veut un résultat STABLE plutôt qu'un
 * résultat dépendant de l'ordre de lecture des champs.
 */
export function regimeSpecialDe(emp) {
  if (!emp || typeof emp !== 'object') return null;
  const statuts = _champsStatut(emp).map(_norm).filter(Boolean);
  const contrats = _champsContrat(emp).map(_norm).filter(Boolean);
  for (const r of REGIMES_SPECIAUX) {
    if (statuts.some((v) => r.valeurs.includes(v))) return r.code;
    if (r.motifTexte && contrats.some((v) => r.motifTexte.test(v))) return r.code;
    // Le champ de statut peut lui aussi porter un libellé (fiches importées).
    if (r.motifTexte && statuts.some((v) => r.motifTexte.test(v))) return r.code;
  }
  return null;
}

/**
 * Quel régime spécial, sur des valeurs EXACTES seulement — statut OU type de contrat, jamais un motif de texte libre.
 *
 * Pourquoi une seconde fonction (audit 360° point 1, 06/10/2026) : `regimeSpecialDe` est volontairement généreuse — elle lit aussi des
 * libellés (`/flexi/` attrape « horaire flexible »). C'est le bon réflexe pour DÉCLARER un régime ; c'est le mauvais pour REFUSER une
 * fiche, car un faux positif bloquerait la paie d'un salarié ordinaire. Le refus ne s'appuie que sur ce que l'écran écrit réellement
 * (« flexijob », « article17 »…) ou sur la valeur exacte d'un type de contrat.
 * @returns {string|null} code du régime, ou null
 */
export function regimeSpecialExact(emp) {
  if (!emp || typeof emp !== 'object') return null;
  const champs = [..._champsStatut(emp), ..._champsContrat(emp)].map(_norm).filter(Boolean);
  for (const r of REGIMES_SPECIAUX) {
    if (champs.some((v) => r.valeurs.includes(v))) return r.code;
  }
  return null;
}

/** Le travailleur est-il sous CE régime ? */
export function estRegimeSpecial(emp, code) {
  return regimeSpecialDe(emp) === code;
}

/** Un régime spécial, quel qu'il soit ? (utile pour « ne pas traiter en ordinaire ») */
export function aUnRegimeSpecial(emp) {
  return regimeSpecialDe(emp) !== null;
}

// ── Prédicats nommés — un par régime ───────────────────────────────────────
// Ils existent pour que les appelants n'écrivent JAMAIS la chaîne du code en
// dur : `estApprenti(emp)` se relit, `regimeSpecialDe(emp) === 'apprenti'` se
// recopie mal. Les deux régimes qui avaient déjà leur source unique
// (`etudiant-fiche.js`, `flexi-engine.js`) délèguent ici : un seul lecteur de la
// fiche, dix questions.
export const estEtudiantRegime  = (emp) => estRegimeSpecial(emp, 'etudiant');
export const estFlexiRegime     = (emp) => estRegimeSpecial(emp, 'flexijob');
export const estApprenti        = (emp) => estRegimeSpecial(emp, 'apprenti');
export const estInterimaire     = (emp) => estRegimeSpecial(emp, 'interimaire');
export const estExtraHoreca     = (emp) => estRegimeSpecial(emp, 'extras_horeca');
export const estArticle17       = (emp) => estRegimeSpecial(emp, 'article17');
export const estPensionneActif  = (emp) => estRegimeSpecial(emp, 'pensionneActif');
export const estArtiste         = (emp) => estRegimeSpecial(emp, 'artiste');
export const estSportif         = (emp) => estRegimeSpecial(emp, 'sportif');
export const estVolontaire      = (emp) => estRegimeSpecial(emp, 'volontaire');
